// src/pages/buyer/MessagesPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, Send, Archive } from 'lucide-react'
import { messagingApi } from '../../api/services'
import { PageSpinner, Empty, Button } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function MessagesPage() {
  const qc = useQueryClient()
  const [activeThread, setActiveThread] = useState(null)
  const [reply, setReply] = useState('')

  const { data, isLoading } = useQuery({ queryKey: ['messages'], queryFn: () => messagingApi.inbox() })
  const { data: threadData } = useQuery({
    queryKey: ['message-thread', activeThread],
    queryFn: () => messagingApi.show(activeThread),
    enabled: !!activeThread,
  })

  const sendReply = useMutation({
    mutationFn: () => messagingApi.reply(activeThread, { body: reply }),
    onSuccess: () => { setReply(''); qc.invalidateQueries(['message-thread', activeThread]) },
    onError: () => toast.error('Failed to send'),
  })

  const archive = useMutation({
    mutationFn: (id) => messagingApi.archive(id),
    onSuccess: () => { toast.success('Archived'); qc.invalidateQueries(['messages']); setActiveThread(null) },
  })

  if (isLoading) return <PageSpinner />
  const threads = data?.data?.data?.threads || data?.data?.data || []
  const thread = threadData?.data?.data || null
  const messages = thread?.messages || []

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-96 gap-0 border border-border rounded-2xl overflow-hidden bg-white">
      {/* Thread list */}
      <div className="w-72 flex-shrink-0 border-r border-border overflow-y-auto">
        <div className="p-4 border-b border-border">
          <h2 className="font-bold text-sm flex items-center gap-2"><MessageCircle size={16} className="text-pink" /> Messages</h2>
        </div>
        {threads.length === 0 && <Empty icon="💬" title="No messages" description="Start a conversation from a product page." />}
        {threads.map(t => (
          <button key={t.id} onClick={() => setActiveThread(t.id)}
            className={`w-full text-left px-4 py-3 border-b border-border hover:bg-surface transition-colors ${activeThread === t.id ? 'bg-pink-light' : ''}`}>
            <p className="text-sm font-bold truncate">{t.supplier?.business_name || t.supplier?.name || 'Supplier'}</p>
            {t.product && <p className="text-xs text-muted truncate">{t.product.name}</p>}
            {t.last_message && <p className="text-xs text-gray-500 truncate mt-0.5">{t.last_message}</p>}
            {t.buyer_unread_count > 0 && <span className="text-xs bg-pink text-white rounded-full px-1.5 py-0.5 mt-1 inline-block">{t.buyer_unread_count}</span>}
          </button>
        ))}
      </div>

      {/* Thread detail */}
      <div className="flex-1 flex flex-col min-w-0">
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center text-muted text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <p className="font-bold text-sm">{thread?.supplier?.business_name || thread?.supplier?.name}</p>
                {thread?.product && <p className="text-xs text-muted">{thread.product.name}</p>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => archive.mutate(activeThread)}><Archive size={14} /></Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'buyer' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${msg.sender_type === 'buyer' ? 'bg-gradient-brand text-white' : 'bg-surface border border-border text-gray-800'}`}>
                    {msg.body}
                    <p className={`text-xs mt-1 ${msg.sender_type === 'buyer' ? 'text-white/70' : 'text-muted'}`}>{new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-border flex gap-2">
              <input className="input flex-1" placeholder="Type a message…" value={reply} onChange={e => setReply(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && reply.trim() && sendReply.mutate()} />
              <Button size="sm" disabled={!reply.trim()} loading={sendReply.isPending} onClick={() => sendReply.mutate()}>
                <Send size={14} />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
